import { useState } from "react";
import { format } from "date-fns";
import { X, Building2, Coins, Calendar, Shield, Zap, Snowflake, ChevronDown } from "lucide-react";
import { UserLedgerTable } from "./UserLedgerTable";
import { AdjustKoinsDialog } from "./AdjustKoinsDialog";
import { useLocale } from "../../../context/LanguageContext";
import {
  ROLE_LABEL_KEYS,
  TIER_LABEL_KEYS,
  ACCOUNT_STATUS_LABEL_KEYS,
  ORG_ROLE_LABEL_KEYS,
} from "../types";
import type { AdminUserDetail, AdminUserOrganization } from "../types";

const ROLE_BADGE_COLORS: Record<string, string> = {
  ADMIN: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  MODERATOR:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  ORG_ADMIN:
    "bg-secondary/10 text-secondary",
  USER: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "text-success",
  PROVISIONED: "text-yellow-500",
};

interface UserDetailPanelProps {
  userDetail: AdminUserDetail | null;
  isLoading: boolean;
  onClose: () => void;
}

export function UserDetailPanel({
  userDetail,
  isLoading,
  onClose,
}: UserDetailPanelProps) {
  const { t } = useLocale();
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [orgsExpanded, setOrgsExpanded] = useState(false);

  if (isLoading || !userDetail) {
    return (
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="h-6 w-40 animate-pulse rounded bg-muted" />
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-10 animate-pulse rounded bg-muted"
            />
          ))}
        </div>
      </div>
    );
  }

  const { user, organizations, totalLedgerEntries, accountStatus } =
    userDetail;
  const statusValue = user.isActive ? accountStatus : "DEACTIVATED";

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              `${user.firstName[0]}${user.lastName[0]}`
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {user.firstName} {user.lastName}
            </h2>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 gap-3 border-b border-border p-5 sm:grid-cols-4">
        <div className="rounded-lg border border-border p-3 text-center">
          <Coins className="mx-auto mb-1 h-4 w-4 text-koins" />
          <p className="text-lg font-bold text-koins">
            {user.koinBalance.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">{t("users.field.balance")}</p>
        </div>
        <div className="rounded-lg border border-border p-3 text-center">
          <Coins className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
          <p className="text-lg font-bold text-foreground">
            {user.lifetimeKoins.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground">{t("users.detail.statLifetime")}</p>
        </div>
        <div className="rounded-lg border border-border p-3 text-center">
          <Zap className="mx-auto mb-1 h-4 w-4 text-orange-500" />
          <p className="text-lg font-bold text-foreground">
            {user.currentStreakDays}
          </p>
          <p className="text-xs text-muted-foreground">{t("users.detail.statStreakDays")}</p>
        </div>
        <div className="rounded-lg border border-border p-3 text-center">
          <Snowflake className="mx-auto mb-1 h-4 w-4 text-sky-500" />
          <p className="text-lg font-bold text-foreground">
            {user.streakFreezesAvailable}
          </p>
          <p className="text-xs text-muted-foreground">{t("users.detail.statStreakFreezes")}</p>
        </div>
      </div>

      {/* Profile Info */}
      <div className="space-y-3 border-b border-border px-5 py-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("users.field.role")}</span>
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              ROLE_BADGE_COLORS[user.role] ?? ROLE_BADGE_COLORS.USER
            }`}
          >
            <Shield className="mr-1 h-3 w-3" />
            {t(ROLE_LABEL_KEYS[user.role] ?? user.role)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("users.field.tier")}</span>
          <span className="text-sm font-medium text-foreground">
            {t(TIER_LABEL_KEYS[user.tier] ?? user.tier)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("users.field.status")}</span>
          <span
            className={`text-sm font-medium ${
              STATUS_COLORS[accountStatus] ?? "text-foreground"
            }`}
          >
            {t(ACCOUNT_STATUS_LABEL_KEYS[statusValue] ?? statusValue)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("users.field.onboarding")}</span>
          <span className="text-sm font-medium text-foreground">
            {t("users.detail.onboardingStep", { step: user.onboardingStep })}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("users.field.joined")}</span>
          <span className="text-sm text-foreground">
            <Calendar className="mr-1 inline h-3 w-3" />
            {format(new Date(user.createdAt), "MMM d, yyyy")}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t("users.field.lastLogin")}</span>
          <span className="text-sm text-foreground">
            {user.lastLoginAt
              ? format(new Date(user.lastLoginAt), "MMM d, yyyy HH:mm")
              : "—"}
          </span>
        </div>
      </div>

      {/* Organizations */}
      {organizations.length > 0 && (
        <div className="border-b border-border px-5 py-4">
          {organizations.length === 1 ? (
            <>
              <h3 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
                <Building2 className="h-4 w-4" />
                {t("users.detail.organizations", {
                  count: organizations.length,
                })}
              </h3>
              <div className="space-y-2">
                {organizations.map((org: AdminUserOrganization) => (
                  <div
                    key={org.organizationId}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {org.organizationName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("users.detail.orgJoined", {
                          date: format(new Date(org.joinedAt), "MMM d, yyyy"),
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        {t(ORG_ROLE_LABEL_KEYS[org.role] ?? org.role)}
                      </span>
                      {!org.isActive && (
                        <span className="text-xs text-destructive">{t("users.detail.orgInactive")}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setOrgsExpanded((v) => !v)}
                className="flex w-full items-center justify-between text-sm font-medium text-foreground"
              >
                <span className="flex items-center gap-1.5">
                  <Building2 className="h-4 w-4" />
                  {t("users.detail.organizations", {
                    count: organizations.length,
                  })}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground transition-transform ${
                    orgsExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>
              {orgsExpanded && (
                <div className="mt-2 space-y-2">
                  {organizations.map((org: AdminUserOrganization) => (
                    <div
                      key={org.organizationId}
                      className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {org.organizationName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("users.detail.orgJoined", {
                            date: format(new Date(org.joinedAt), "MMM d, yyyy"),
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">
                          {t(ORG_ROLE_LABEL_KEYS[org.role] ?? org.role)}
                        </span>
                        {!org.isActive && (
                          <span className="text-xs text-destructive">{t("users.detail.orgInactive")}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Koin Ledger */}
      <div className="px-5 py-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            <Coins className="h-4 w-4 text-koins" />
            {t("users.detail.ledgerTitle", { count: totalLedgerEntries })}
          </h3>
          <button
            type="button"
            onClick={() => setShowAdjustDialog(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground hover:bg-muted"
          >
            ±&nbsp;{t("users.detail.adjustBalance")}
          </button>
        </div>

        <UserLedgerTable userId={user._id} />
      </div>

      {/* Adjust Koins Dialog */}
      {showAdjustDialog && (
        <AdjustKoinsDialog
          userId={user._id}
          userName={`${user.firstName} ${user.lastName}`}
          currentBalance={user.koinBalance}
          onClose={() => setShowAdjustDialog(false)}
        />
      )}
    </div>
  );
}
